// Typed API client seam. Access token lives in memory; the refresh token in localStorage.
// On a 401 the client does a single-flight /auth/refresh and replays the original request.
// This mirrors the crims rebuild's api/client.ts so a generated OpenAPI client can drop in later.
import * as Sentry from '@sentry/react'

const BASE = (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE ?? '/api'
const REFRESH_KEY = 'pc.refresh_token'

let accessToken: string | null = null
// Bumped on every tokens.clear() (i.e. logout / auth-lost). A refresh that started before a clear checks this
// and discards its result instead of re-persisting tokens — otherwise an in-flight /auth/refresh resolving just
// after Sign out would re-write a refresh token into localStorage and silently sign the user back in on reload.
let authEpoch = 0

export const tokens = {
  getAccess: () => accessToken,
  setAccess: (t: string | null) => {
    accessToken = t
  },
  getRefresh: () => {
    try {
      return localStorage.getItem(REFRESH_KEY)
    } catch {
      return null
    }
  },
  setRefresh: (t: string | null) => {
    try {
      if (t) localStorage.setItem(REFRESH_KEY, t)
      else localStorage.removeItem(REFRESH_KEY)
    } catch {
      /* ignore */
    }
  },
  clear: () => {
    accessToken = null
    authEpoch++ // fence any in-flight refresh so it can't re-persist tokens after logout
    try {
      localStorage.removeItem(REFRESH_KEY)
    } catch {
      /* ignore */
    }
  },
}

export type Problem = { title?: string; status?: number; code?: string; detail?: string }

export class ApiError extends Error {
  status: number
  code?: string
  constructor(status: number, message: string, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

let onAuthLost: (() => void) | null = null
export function setOnAuthLost(fn: () => void) {
  onAuthLost = fn
}

// subscribeCrossTabLogout fires onLogout when ANOTHER tab logs out. A logout removes the refresh token from
// localStorage, which raises a `storage` event in every other tab. We act only on removal (newValue == null) —
// a token ROTATION writes a new non-null value and must be ignored. Returns an unsubscribe.
export function subscribeCrossTabLogout(onLogout: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === REFRESH_KEY && !e.newValue) onLogout()
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

export type RefreshOutcome = 'ok' | 'rejected' | 'transient'
let refreshInflight: Promise<RefreshOutcome> | null = null

export async function refreshAccessToken(): Promise<RefreshOutcome> {
  if (refreshInflight) return refreshInflight
  refreshInflight = (async () => {
    const epoch = authEpoch // snapshot; if a logout (tokens.clear) happens mid-flight, discard the result below
    const rt = tokens.getRefresh()
    if (!rt) return 'rejected'
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      })
      if (res.status === 401 || res.status === 403) return 'rejected'
      if (!res.ok) return 'transient'
      const data = (await res.json()) as { access_token?: string; refresh_token?: string }
      if (epoch !== authEpoch) return 'rejected' // logged out while refreshing — do NOT re-persist tokens
      if (data.access_token) tokens.setAccess(data.access_token)
      if (data.refresh_token) tokens.setRefresh(data.refresh_token)
      return data.access_token ? 'ok' : 'rejected'
    } catch {
      return 'transient'
    } finally {
      refreshInflight = null
    }
  })()
  return refreshInflight
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

async function raw(method: Method, path: string, body?: unknown, retry = true): Promise<Response> {
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (res.status === 401 && retry) {
    const outcome = await refreshAccessToken()
    if (outcome === 'ok') return raw(method, path, body, false)
    if (outcome === 'rejected' && onAuthLost) onAuthLost()
  }
  return res
}

async function toResult<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T
  const text = await res.text()
  // Tolerate non-JSON bodies (e.g. a plain-text "404 page not found" from an unknown route) — parse
  // defensively so a bad/missing endpoint yields a clean error instead of a cryptic JSON.parse crash.
  let data: unknown
  try {
    data = text ? JSON.parse(text) : undefined
  } catch {
    data = undefined
  }
  if (!res.ok) {
    const p = (data ?? {}) as Problem
    const msg = p.detail || p.title || (data === undefined ? text.trim() : '') || `Request failed (${res.status})`
    const err = new ApiError(res.status, msg, p.code)
    // Report server-side failures to Sentry (no-op when unconfigured). Skip 4xx/401 — expected auth/validation.
    if (res.status >= 500) Sentry.captureException(err)
    throw err
  }
  if (text && data === undefined) {
    throw new ApiError(res.status, `Unexpected non-JSON response from ${res.url}`, 'bad_response')
  }
  return data as T
}

export type Paged<T> = { items: T[]; total: number }

export const api = {
  get: <T>(path: string) => raw('GET', path).then((r) => toResult<T>(r)),
  /** GET a paginated list — reads the total from the X-Total-Count header (set by httpx.SetTotal). */
  getPaged: async <T>(path: string): Promise<Paged<T>> => {
    const r = await raw('GET', path)
    const total = Number(r.headers.get('X-Total-Count') ?? '0')
    const items = await toResult<T[]>(r)
    return { items: items ?? [], total: Number.isFinite(total) ? total : 0 }
  },
  post: <T>(path: string, body?: unknown) => raw('POST', path, body ?? {}).then((r) => toResult<T>(r)),
  patch: <T>(path: string, body?: unknown) => raw('PATCH', path, body ?? {}).then((r) => toResult<T>(r)),
  del: <T>(path: string) => raw('DELETE', path).then((r) => toResult<T>(r)),
  /** unauthenticated POST (login / otp) — never attaches a token or triggers refresh. */
  postPublic: <T>(path: string, body: unknown) =>
    fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => toResult<T>(r)),
}
